import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function DeadlineBell() {
  const { tenantId, organizations } = useTenant();
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  const load = () => {
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/deadlines${p}`).then((r) => {
      const a = (r.data || []).filter((d) => d.scaduta || d.in_avvicinamento);
      a.sort((x, y) => (x.giorni_rimanenti ?? 999) - (y.giorni_rimanenti ?? 999));
      setAlerts(a);
    }).catch(() => setAlerts([]));
  };
  useEffect(load, [tenantId]);

  const overdue = alerts.filter((d) => d.scaduta).length;
  const count = alerts.length;
  const orgName = (id) => organizations.find((o) => o.id === id)?.nome;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="deadline-bell" aria-label="Avvisi scadenze"
          className="relative flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:border-primary/50 hover:text-slate-700 transition-colors">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span data-testid="deadline-bell-badge"
              className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold text-white ${overdue > 0 ? "bg-red-600 animate-pulse" : "bg-amber-500"}`}>
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-1.5rem))] p-0" data-testid="deadline-bell-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-semibold">Avvisi scadenze</span>
          <span className="text-xs text-slate-500">{overdue} scadute · {count - overdue} in arrivo</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {count === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Nessuna scadenza in avvicinamento (30 giorni) o scaduta.</p>
          ) : alerts.map((d) => {
            const gg = d.giorni_rimanenti;
            const late = d.scaduta;
            return (
              <button key={d.id} data-testid={`deadline-alert-${d.id}`} onClick={() => navigate("/scadenze")}
                className="w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                {late ? <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" /> : <Clock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 truncate">{d.descrizione || d.tipo}</p>
                  <p className="text-xs text-slate-500">
                    {d.data_scadenza}
                    {" · "}
                    <span className={late ? "text-red-600 font-medium" : "text-amber-600 font-medium"}>
                      {late ? `scaduta da ${Math.abs(gg)} gg` : (gg === 0 ? "scade oggi" : `tra ${gg} gg`)}
                    </span>
                    {!tenantId && orgName(d.tenant_id) ? ` · ${orgName(d.tenant_id)}` : ""}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        <button onClick={() => navigate("/scadenze")} data-testid="deadline-bell-all"
          className="w-full px-4 py-2.5 text-sm font-medium text-primary hover:bg-slate-50 border-t border-slate-100">
          Apri l'agenda scadenze →
        </button>
      </PopoverContent>
    </Popover>
  );
}
